import './item-edit-screen.scss'

import { defineComponent, markRaw } from 'vue'
// type-only import: entity-ui's own tsconfig scope has no file importing vue-router, so without this
// vue-router's global $route/$router augmentation of the Vue instance type never gets loaded here
import type {} from 'vue-router'
import { faCheck, faEdit, faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { TranslateResult } from 'vue-i18n'

import { EntityField, EntityFieldStatus, Wemi } from '@skinsoft/types'
import { wemi_metaRecordMask } from '@skinsoft/record-mask-configs'
import { WemiUtils } from '@skinsoft/wemi-utils'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { RecordMaskDisplay } from '@skinsoft/record-mask'
import { GenericEditScreen, GenericEditScreenSection } from '@skinsoft/pages/generic/generic-edit-screen'
import { ItemCreationImagePopup } from '../item-creation-image-popup'
import { ItemEditScreenHeader } from '../item-edit-screen-header'
import { ItemEditScreenController } from '@skinsoft/view-controller'

import {
    DELETE_IMAGE_CLICK_TOPIC,
    DISPLAY_ALERT_TOPIC,
    DELETE_MODIFICATIONS_NOT_SYNCHRONIZED,
    DELETE_NOT_SYNCHRONIZED_NEW_ITEM,
    SAVE_CURRENT_ITEM_TOPIC,
    MEDIA_END_LOADING_TOPIC,
    IMAGE_UPLOADED_TOPIC,
    ITEM_CREATION_IMG_POPUP_ADD_MEDIA,
    SAVE_GENERIC_EDIT_SCREEN,
} from '@skinsoft/bus'
import { DeleteImageClickEvent } from '@skinsoft/entity-ui/src/media/medias-area'
import { AddMediaEvent } from '@skinsoft/entity-ui/src/media/medias-fields-area'
import { MaskFieldUpdateEvent } from '@skinsoft/record-mask/src/components/mask-field'

declare const OFFLINE_DISABLED_MASK_FIELDS: { entityId: string, message?: string }[]

export const ItemEditScreen = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        itemCreationImagePopup: ItemCreationImagePopup,
        sGenericEditScreen: GenericEditScreen,
        sItemEditScreenHeader: ItemEditScreenHeader,
    },
    props: {
        itemId: { type: String },
        headerTitle: { type: String },
        navigateBackRoute: { type: String },
    },
    template: require('./item-edit-screen.html'),
    data() {
        return {
            viewController: null as ItemEditScreenController | null,

            forceReadonly: true,
            canEdit: true,
            imageChoice: false,

            fieldsErrors: [] as { entityId: string, message: string }[],
            disabledMaskFields: [] as { entityId: string, message?: string }[],

            isOnline: window.navigator.onLine,

            // never read via `this.`, only the imported constant is used directly as an object key
            // below; kept as data to preserve original behaviour
            DELETE_IMAGE_CLICK_TOPIC,
            windowWidth: window.innerWidth,
        }
    },
    mounted() {
        this.viewController = new ItemEditScreenController(() => {
            this.forceReadonly = false
            this.fieldsErrors = [{
                entityId: '5c50161dca5d4700015b3c92',
                message: this.$t('editItemScreen.errorMessages.inventoryNumberNotUnique') as string,
            }]
        })

        this.subscribeToBus(SAVE_CURRENT_ITEM_TOPIC, () => {
            this.fieldsErrors = []
            this.emitOnBus(SAVE_GENERIC_EDIT_SCREEN)
        })

        this.subscribeToBus(DELETE_NOT_SYNCHRONIZED_NEW_ITEM, this.onDeleteItem)
        this.subscribeToBus(DELETE_MODIFICATIONS_NOT_SYNCHRONIZED, this.onDeleteModifications)
        this.subscribeToBus(IMAGE_UPLOADED_TOPIC, () => { this.canEdit = false })
        this.subscribeToBus(MEDIA_END_LOADING_TOPIC, () => { this.canEdit = true })

        this.subscribeToBus(ITEM_CREATION_IMG_POPUP_ADD_MEDIA, event => {
            const payload: AddMediaEvent = event.payload
            const entity = (this.$refs.genericEditScreen as any)?.entity
            if (entity && this.viewController) {
                this.viewController.addImage(payload.newMediaId || payload.cacheKey, entity)
            }
        })

        this.imageChoice = this.$route.query.imageChoice === 'true'

        this.disabledMaskFields = OFFLINE_DISABLED_MASK_FIELDS.map(field => ({
            ...field,
            message: field.message ? this.$t(field.message) as string : undefined,
        }))

        window.addEventListener('online', this.updateConnexionStatus)
        window.addEventListener('offline', this.updateConnexionStatus)
        window.addEventListener('resize', this.updateWindowWidth)
    },
    beforeUnmount() {
        window.removeEventListener('online', this.updateConnexionStatus)
        window.removeEventListener('offline', this.updateConnexionStatus)
        window.removeEventListener('resize', this.updateWindowWidth)
    },
    computed: {
        isReady(): boolean {
            return this.viewController !== null
        },
        entity(): Wemi | undefined {
            return (this.$refs.genericEditScreen as any)?.entity
        },
        isMainButtonClickable(): boolean {
            return this.canEdit || this.forceReadonly
        },
        buttonLabel(): TranslateResult {
            return this.forceReadonly
                ? this.$t('editItemScreen.editButton')
                : this.$t('editItemScreen.confirmButton')
        },
        buttonIcon(): IconDefinition {
            if (this.isMainButtonClickable) {
                return this.forceReadonly ? faEdit : faCheck
            }
            return faCircleNotch
        },
        buttonColor(): string {
            return this.forceReadonly ? '' : 'validate-color'
        },
        buttonIconSize(): string {
            return this.windowWidth < 450 ? 's' : '2x'
        },
        wemiSections(): GenericEditScreenSection[] {
            return [{
                id: 'wemi-meta',
                // markRaw: without it, this feeds GenericEditScreen's reactive `sections` array
                // and gets deep-proxied — no benefit, and exactly the overhead Vue warns about.
                component: markRaw(RecordMaskDisplay),
                props: () => ({
                    recordMask: wemi_metaRecordMask,
                    entity: (this.$refs.genericEditScreen as any)?.entity,
                    isMetaMask: true,
                    editing: !this.forceReadonly,
                    getEntityFieldStatus: this.getEntityFieldStatus,
                    forceMediaUploadInCache: true,
                    class: 'item-mask-display',
                }),
                eventListeners: () => ({
                    'value-change': (event: MaskFieldUpdateEvent) => {
                        const entity = (this.$refs.genericEditScreen as any)?.entity
                        if (entity) this.viewController?.updateEntity(event, entity)
                    },
                    'addMedia': (addedMedia: AddMediaEvent) => {
                        const entity = (this.$refs.genericEditScreen as any)?.entity
                        if (entity) this.viewController?.addImage(addedMedia.cacheKey, entity)
                    },
                    'imageChoice': (mediaId: string) => {
                        this.applyImage(mediaId)
                    },
                    [DELETE_IMAGE_CLICK_TOPIC]: (event: DeleteImageClickEvent) => {
                        const entity = (this.$refs.genericEditScreen as any)?.entity
                        if (entity && event.imageId) this.viewController?.deleteImage(event.imageId, entity)
                    },
                }),
            }]
        },
    },
    methods: {
        updateConnexionStatus() {
            this.isOnline = window.navigator.onLine
        },
        updateWindowWidth() {
            this.windowWidth = window.innerWidth
        },
        onMainButtonClick() {
            if (this.canEdit) {
                this.forceReadonly = !this.forceReadonly
                if (this.forceReadonly) {
                    this.fieldsErrors = []
                    this.emitOnBus(SAVE_GENERIC_EDIT_SCREEN)
                }
            } else if (this.forceReadonly) {
                this.forceReadonly = !this.forceReadonly
            }
        },
        applyImage(image: string) {
            this.imageChoice = false
            const entity = (this.$refs.genericEditScreen as any)?.entity
            if (entity && this.viewController) {
                this.viewController.setFirstMedia(image, entity)
            }
        },
        async onDeleteItem() {
            const entity: Wemi = (this.$refs.genericEditScreen as any)?.entity
            if (entity) {
                try {
                    await WemiUtils.deleteWemi(entity)
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.$t('editItemScreen.deleteNotification.successMessage'),
                        variant: 'success',
                    })
                } catch {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.$t('editItemScreen.deleteNotification.errorMessage'),
                        variant: 'danger',
                    })
                }
            }
            this.$router.push({ name: this.navigateBackRoute })
        },
        async onDeleteModifications() {
            const entity: Wemi = (this.$refs.genericEditScreen as any)?.entity
            if (entity) {
                try {
                    await WemiUtils.deleteModificationsWemi(entity)
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.$t('editItemScreen.deleteModificationsNotification.successMessage'),
                        variant: 'success',
                    })
                    ;(this.$refs.genericEditScreen as any)?.fetchEntity()
                } catch {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: this.$t('editItemScreen.deleteModificationsNotification.errorMessage'),
                        variant: 'danger',
                    })
                }
            }
        },
        getEntityFieldStatus(entityField: EntityField): EntityFieldStatus {
            const result: EntityFieldStatus = { disabled: false, error: false }

            if (!this.isOnline) {
                const disabledField = this.disabledMaskFields?.find(f => {
                    return f.entityId === (entityField as any).entityFieldId
                })
                if (disabledField) {
                    result.disabled = true
                    if (disabledField.message) result.disabledMessage = disabledField.message
                }
            }

            const errorField = this.fieldsErrors?.find(e => {
                return e.entityId === (entityField as any).entityFieldId
            })
            if (errorField) {
                result.error = true
                result.errorMessage = errorField.message
            }

            return result
        },
    },
})
